/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChamberAssignDialog from '../../admin/ChamberAssignDialog';

/**
 * Panel shown inside the admin review dialog for `cart_request` requests.
 * - Lists current (Active) and past (Completed/Terminated) chamber assignments.
 * - Highlights the assignment the request targets.
 * - Provides an "Adjust Chamber Assignment" button that opens the existing
 *   ChamberAssignDialog. The button is not gated on the request's status: an
 *   admin working a Pending request often has to adjust the assignment before
 *   marking it Completed.
 */
const CartRequestAssignmentsPanel = ({ request, onChanged }) => {
  const [project, setProject] = useState(null);
  const [active, setActive] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const projectId = typeof request.project_id === 'object'
    ? request.project_id?._id
    : request.project_id;

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [projRes, assignRes] = await Promise.all([
        axios.get(`/api/projects/${projectId}`),
        axios.get(`/api/projects/${projectId}/assignments`)
      ]);
      setProject(projRes.data);
      const activeRows = [];
      const pastRows = [];
      (assignRes.data?.assignments || []).forEach((ca) => {
        const chamberName = ca.chamber?.chamber || 'Compartment';
        const unitShort = ca.chamber?.capacity?.unit?.unit_short || '';
        (ca.assignments || []).forEach((a) => {
          const row = {
            _id: a._id,
            chamber_name: chamberName,
            unit_short: unitShort,
            rate_id: a.rate_id,
            quantity: a.quantity,
            start_date: a.start_date,
            end_date: a.end_date,
            status: a.status
          };
          if (a.status === 'Active') activeRows.push(row);
          else pastRows.push(row);
        });
      });
      setActive(activeRows);
      setPast(pastRows);
    } catch (err) {
      console.error('Error loading project assignments:', err);
      setError('Failed to load compartment assignments');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const targetAssignmentId = request.form_data?.assignment?.id;

  const renderRow = (a) => {
    const isTarget = String(a._id) === String(targetAssignmentId);
    return (
      <TableRow key={String(a._id)} sx={isTarget ? { bgcolor: 'warning.50' } : {}}>
        <TableCell>{a.chamber_name}{isTarget && <Chip size="small" label="Targeted" color="warning" sx={{ ml: 1 }} />}</TableCell>
        <TableCell>{a.rate_id}</TableCell>
        <TableCell>{a.quantity} {a.unit_short || ''}</TableCell>
        <TableCell>{a.start_date ? new Date(a.start_date).toLocaleDateString() : '—'}</TableCell>
        <TableCell>{a.end_date ? new Date(a.end_date).toLocaleDateString() : 'Ongoing'}</TableCell>
        <TableCell><Chip size="small" label={a.status} /></TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Project Compartment Assignments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {request.form_data?.modification === 'add' ? 'Requested add' : request.form_data?.modification === 'remove' ? 'Requested remove' : 'Modification'}: <strong>{request.form_data?.numberOfCarts || 0}</strong> {request.form_data?.cartType ? `${request.form_data.cartType} carts` : 'carts'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            disabled={!project}
            onClick={() => setDialogOpen(true)}
            sx={{
              textTransform: 'none',
              fontSize: '0.72rem',
              borderColor: '#cbd5e1',
              color: '#475569',
              '&:hover': {
                borderColor: '#94a3b8',
                backgroundColor: 'rgba(100, 116, 139, 0.06)'
              }
            }}
          >
            Adjust
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : (
        <>
          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2">Active Assignments</Typography>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Compartment</TableCell>
                    <TableCell>Rate</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {active.length > 0
                    ? active.map(renderRow)
                    : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                          No active assignments
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined">
            <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2">Past Assignments</Typography>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Compartment</TableCell>
                    <TableCell>Rate</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {past.length > 0
                    ? past.map(renderRow)
                    : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                          No past assignments
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {dialogOpen && project && (
        <ChamberAssignDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          application={project}
          onSave={() => {
            setDialogOpen(false);
            load();
            if (onChanged) onChanged();
          }}
        />
      )}
    </Box>
  );
};

export default CartRequestAssignmentsPanel;
